/**
 * Memory Admin API 服务
 * 封装 axiom-memory-admin 服务的 API 调用
 * 基于 memory-admin-api.md 协议文档
 */

import { ApiClient, apiClients } from '@/shared/services/api'
import type {
  Entity,
  EntitiesResponse,
  EntityQueryParams,
  Relationship,
  RelationshipsResponse,
  RelationshipQueryParams,
  GraphDataResponse,
  GraphQueryParams,
  Session,
  SessionsResponse,
  SessionQueryParams,
  MessagesResponse,
  ExtractionResult,
  EntityStats,
  RelationshipStats,
  SessionStats,
  UserMemoryItem,
} from '../types/memory'

/**
 * Memory Admin API 服务
 */
export const memoryAdminApi = {
  // ==================== 实体管理 ====================

  /**
   * 查询实体列表
   */
  async getEntities(params: EntityQueryParams): Promise<EntitiesResponse> {
    const response = await ApiClient.getWithClient<EntitiesResponse>(
      apiClients.memoryAdmin,
      '/entities',
      { params }
    )
    return response.data
  },

  /**
   * 获取单个实体
   */
  async getEntity(entityId: string, userId: string): Promise<Entity> {
    const response = await ApiClient.getWithClient<Entity>(
      apiClients.memoryAdmin,
      `/entities/${entityId}`,
      { params: { user_id: userId } }
    )
    return response.data
  },

  /**
   * 获取实体统计
   */
  async getEntityStats(userId: string, projectId?: string): Promise<EntityStats> {
    const response = await ApiClient.getWithClient<EntityStats>(
      apiClients.memoryAdmin,
      '/entities/stats',
      { params: { user_id: userId, project_id: projectId } }
    )
    return response.data
  },

  // ==================== 关系管理 ====================

  /**
   * 查询关系列表
   */
  async getRelationships(params: RelationshipQueryParams): Promise<RelationshipsResponse> {
    const response = await ApiClient.getWithClient<RelationshipsResponse>(
      apiClients.memoryAdmin,
      '/relationships',
      { params }
    )
    return response.data
  },

  /**
   * 获取单个关系
   */
  async getRelationship(relationshipId: string, userId: string): Promise<Relationship> {
    const response = await ApiClient.getWithClient<Relationship>(
      apiClients.memoryAdmin,
      `/relationships/${relationshipId}`,
      { params: { user_id: userId } }
    )
    return response.data
  },

  /**
   * 获取关系统计
   */
  async getRelationshipStats(userId: string, projectId?: string): Promise<RelationshipStats> {
    const response = await ApiClient.getWithClient<RelationshipStats>(
      apiClients.memoryAdmin,
      '/relationships/stats',
      { params: { user_id: userId, project_id: projectId } }
    )
    return response.data
  },

  // ==================== 图谱管理 ====================

  /**
   * 获取图谱数据（用于可视化）
   */
  async getGraphData(params: GraphQueryParams): Promise<GraphDataResponse> {
    const response = await ApiClient.getWithClient<GraphDataResponse>(
      apiClients.memoryAdmin,
      '/graph',
      { params }
    )
    return response.data
  },

  // ==================== 会话管理 ====================
  // 按照后端规范：使用 /chat/sessions 路径

  /**
   * 查询会话列表
   * GET /api/v1/chat/sessions
   */
  async getSessions(params: SessionQueryParams): Promise<SessionsResponse> {
    const response = await ApiClient.getWithClient<SessionsResponse>(
      apiClients.memoryAdmin,
      '/chat/sessions',
      { params }
    )
    return response.data
  },

  /**
   * 获取单个会话
   * GET /api/v1/chat/sessions/{session_id}
   */
  async getSession(sessionId: string, userId: string): Promise<Session> {
    const response = await ApiClient.getWithClient<Session>(
      apiClients.memoryAdmin,
      `/chat/sessions/${sessionId}`,
      { params: { user_id: userId } }
    )
    return response.data
  },

  /**
   * 获取会话消息
   * GET /api/v1/chat/sessions/{session_id}/messages
   */
  async getSessionMessages(sessionId: string, userId: string, limit?: number): Promise<MessagesResponse> {
    const response = await ApiClient.getWithClient<MessagesResponse>(
      apiClients.memoryAdmin,
      `/chat/sessions/${sessionId}/messages`,
      { params: { user_id: userId, limit } }
    )
    return response.data
  },

  /**
   * 获取会话提取结果
   * GET /api/v1/chat/sessions/{session_id}/extraction
   * 返回完整的提取结果，包含实体和关系列表
   */
  async getSessionExtraction(sessionId: string, userId: string): Promise<ExtractionResult> {
    const response = await ApiClient.getWithClient<ExtractionResult>(
      apiClients.memoryAdmin,
      `/chat/sessions/${sessionId}/extraction`,
      { params: { user_id: userId } }
    )
    return response.data
  },

  /**
   * 手动触发提取
   * POST /api/v1/chat/sessions/{session_id}/extract
   */
  async extractSession(sessionId: string, userId: string): Promise<{ message: string; status: string }> {
    const response = await ApiClient.postWithClient<{ message: string; status: string }>(
      apiClients.memoryAdmin,
      `/chat/sessions/${sessionId}/extract`,
      { user_id: userId }
    )
    return response.data
  },

  /**
   * 获取会话统计
   * GET /api/v1/chat/sessions/stats
   */
  async getSessionStats(userId: string, projectId?: string): Promise<SessionStats> {
    const response = await ApiClient.getWithClient<SessionStats>(
      apiClients.memoryAdmin,
      '/chat/sessions/stats',
      { params: { user_id: userId, project_id: projectId } }
    )
    return response.data
  },

  // ==================== 治理操作 ====================

  /**
   * 清空用户数据（需要管理员权限）
   */
  async clearUserData(userId: string, projectId?: string): Promise<{ message: string }> {
    const response = await ApiClient.deleteWithClient<{ message: string }>(
      apiClients.memoryAdmin,
      '/admin/clear',
      { params: { user_id: userId, project_id: projectId } }
    )
    return response.data
  },

  /**
   * 重新提取会话（需要管理员权限）
   * POST /api/v1/chat/sessions/{session_id}/extract
   */
  async reExtractSession(sessionId: string, userId: string): Promise<{ message: string; status: string }> {
    const response = await ApiClient.postWithClient<{ message: string; status: string }>(
      apiClients.memoryAdmin,
      `/chat/sessions/${sessionId}/extract`,
      { user_id: userId }
    )
    return response.data
  },

  // ==================== 记忆管理 ====================

  /**
   * 获取项目下所有用户的记忆统计信息
   * GET /api/v1/memory/projects/{project_id}/users
   */
  async getProjectUsers(projectId: string): Promise<UserMemoryItem[]> {
    const response = await ApiClient.getWithClient<UserMemoryItem[]>(
      apiClients.memoryAdmin,
      `/memory/projects/${projectId}/users`
    )
    return response.data
  },

  /**
   * 获取用户记忆图谱（推荐使用，RESTful 风格）
   * GET /api/v1/memory/users/{user_id}/graph?project_id=xxx
   * 新路由，替代旧的 GET /api/v1/graph?user_id=xxx
   */
  async getUserGraph(userId: string, projectId?: string): Promise<GraphDataResponse> {
    const response = await ApiClient.getWithClient<GraphDataResponse>(
      apiClients.memoryAdmin,
      `/memory/users/${userId}/graph`,
      { params: { project_id: projectId } }
    )
    return response.data
  },

  /**
   * 删除用户记忆
   * DELETE /api/v1/memory/users/{user_id}/memory?project_id=xxx&delete_chat_history=true
   */
  async deleteUserMemory(
    userId: string, 
    projectId: string, 
    deleteChatHistory: boolean = true
  ): Promise<{
    user_id: string
    project_id: string
    entities_deleted: number
    messages_deleted: number
    success: boolean
  }> {
    const response = await ApiClient.deleteWithClient<{
      user_id: string
      project_id: string
      entities_deleted: number
      messages_deleted: number
      success: boolean
    }>(
      apiClients.memoryAdmin,
      `/memory/users/${userId}/memory`,
      { params: { project_id: projectId, delete_chat_history: deleteChatHistory } }
    )
    return response.data
  },
}

